import React, { useEffect } from 'react';
import { setPageTitle } from '../../../utils/title';
import Breadcrumb from '../../../components/Breadcrumb';
import './FinTechPlatform.scss';
import infrastructureDiagram from '../../../assets/images/case-studies/fintech-platform-diagram.png';

const FinTechPlatform = () => {
  useEffect(() => {
    setPageTitle('Case Study - FinTech Platform');
  }, []);

  const breadcrumbItems = [
    { path: '/', label: 'Home' },
    { path: '/case-studies', label: 'Case Studies' },
    { path: '/case-studies/fintech-platform', label: 'FinTech Platform' },
  ];

  return (
    <div className="case-study-page">
      <Breadcrumb items={breadcrumbItems} />
      <div className="case-study">
        <div className="case-study__header">
          <h1>
            FinTech Platform for Data Analysis and Financial Reporting that Revolutionizes Portfolio
            Management in the B2B Energy Sector
          </h1>
          <p className="case-study__preamble">
            DevSecOps Inc. responded to the customer's need to modernize cloud infrastructure
            supporting their legacy FinTech platform, and implemented a scalable, secure, and highly
            available cloud infrastructure based on Amazon ECS, with RDS database, auto-scaling
            groups, secrets management using AWS Secrets Manager and SSM Parameter Store, serverless
            data pipelines, AWS Cognito integration for SSO authentication through client's
            corporate Identity Provider – Okta, and CI/CD integration based on GitHub actions.
          </p>
        </div>

        <div className="case-study__content">
          <section className="case-study__section">
            <h2>Problem Statement</h2>
            <p>
              Our client, a prominent player in the B2B energy sector, faced significant operational
              hurdles due to their legacy system, which had evolved incrementally over many years.
              The core issues they encountered included:
            </p>
            <ul>
              <li>
                An <strong>outdated and slow database</strong> that struggled to keep pace with the
                company's data demands.
              </li>
              <li>
                A <strong>cluttered web application</strong> interface that complicates user
                interaction, increasing the likelihood of user errors.
              </li>
              <li>
                A <strong>disjointed system architecture</strong> where data ingestion and reporting
                functionalities were segregated across different interfaces, hindering efficient
                data management.
              </li>
              <li>
                <strong>Inadequate data experimentation capabilities</strong>, preventing effective
                data preparation for ingestion.
              </li>
              <li>
                <strong>Poor data validation processes</strong> within the old application, leading
                to incomprehensible errors when encountering problematic input data.
              </li>
            </ul>
            <p>
              These challenges not only hampered the company's operational efficiency but also
              impeded their ability to make informed, data-driven decisions.
            </p>
          </section>

          <section className="case-study__section">
            <h2>Our Proposed Solution</h2>
            <p>
              To address these challenges and transform our client's data management and reporting
              capabilities, we developed a new FinTech platform tailored to the needs of the B2B
              energy sector. The revamped platform offers:
            </p>
            <ul>
              <li>
                A <strong>unified interface</strong> that seamlessly integrates data ingestion and
                reporting functionalities, significantly reducing user error and enhancing overall
                user experience.
              </li>
              <li>
                <strong>Advanced data validation</strong> features that not only detect issues but
                also suggest potential fixes, enabling users to address data quality proactively.
              </li>
              <li>
                A <strong>decoupled data analytics architecture</strong> where data cleanup,
                transformation, and ETL (Extract, Transform, Load) processes are isolated from the
                web application. This separation significantly boosts the system's data handling
                capabilities and accommodates larger data volumes more effectively.
              </li>
              <li>
                <strong>Improved data organization</strong> through the segregation of time-series
                data, its metadata, and user database information. This strategic separation
                streamlines data processing and enhances the clarity and accessibility of data
                insights.
              </li>
            </ul>
          </section>

          <section className="case-study__section">
            <h2>Solution Infrastructure Components</h2>
            <p>
              The solution architecture is provisioned through Infrastructure as Code using our
              proprietary ECS solution blueprint, and consists of the following components:
            </p>
            <ul>
              <li>AWS Cognito for user authentication through customer's SSO – Okta</li>
              <li>
                Containerized application layers: Frontend, Backend, GraphQL Hasura middleware
              </li>
              <li>
                Amazon ECS cluster to orchestrate deployment and execution of application services
              </li>
              <li>Auto-scaling group of EC2 instances to host and scale application workloads</li>
              <li>Amazon RDS instance hosting the main database of the platform</li>
              <li>
                AWS Secrets Manager secret to store main application secrets accessible across all
                containers
              </li>
              <li>
                AWS SSM Parameters to store sensitive (secret) and non-sensitive container-specific
                parameters
              </li>
              <li>
                Application Load Balancer to route and balance traffic flowing to application
                containers
              </li>
              <li>S3 Buckets providing storage for platform's data pipelines</li>
              <li>Lambda functions executing data pipelines</li>
            </ul>
            <div className="case-study__diagram">
              <img src={infrastructureDiagram} alt="FinTech Platform Infrastructure Diagram" />
            </div>
          </section>

          <section className="case-study__section">
            <h2>Outcomes</h2>
            <p>
              By implementing these solutions, we have delivered a platform that not only resolves
              the existing inefficiencies and limitations but also provides a robust foundation for
              future innovation and growth in data analysis and financial reporting within the B2B
              energy sector.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FinTechPlatform;
